// 3rd party
import * as React from 'react';
import { inject, observer } from 'mobx-react';

// Bernie
import { AnalyticsStore, ContextStore } from 'bernie-core';

// Shared UI
import { LocalizedComponentProps, LocalizedText, withLocalization } from '@shared-ui/localization-context';

// UI Toolkit
import { UitkPrimaryButton, UitkSecondaryButton } from 'uitk-react-button';
import { UitkIcon } from 'uitk-react-icons';
import { UitkInformativeInput, UitkNumberInput, UitkTextArea, UitkTextInput } from 'uitk-react-input';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkLink } from 'uitk-react-link';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkSelect } from 'uitk-react-select';
import { UitkCheckbox, UitkCheckboxGroup, UitkRadio, UitkRadioGroup } from 'uitk-react-switch';
import { UitkHeading, UitkParagraph } from 'uitk-react-text';

// Models
import { Program, Product } from 'models';

// Stores
import { UserAuthStore } from 'stores';

// Components
import {
  initialTrackingTypeState,
  initialTrackingInformationState,
  initialTravelOptionsState,
  initialDestinationState,
  initialWidthStates,
  initialFixedWidthState,
  initialHeightStates,
  initialFixedHeightState,
  initialBackgroundState,
  initialOpenLinkState,
  initialErrorsState,
} from 'components/search/bex/config';
import {
  TrackingType,
  TravelOption,
  DimensionSizing,
  DimensionUnit,
  Background,
  OpenLink,
} from 'components/search/bex/enums';
import {
  TrackingTypeState,
  TrackingInformationState,
  TravelOptionsState,
  DestinationState,
  WidthState,
  HeightState,
  BackgroundState,
  OpenLinkState,
  ErrorsState,
  DemoLinkState,
  DemoStylesState,
} from 'components/search/bex/interfaces';
import { MoreInformationDialog, MoreInformationDialogTriggerButton } from 'components/shared/more-information';

// Utils
import { getProgramAnalyticsMicroMessage, getProductAnalyticsMicroMessage } from '../../util/analytics';

interface BexSearchProps extends LocalizedComponentProps {
  analytics?: AnalyticsStore;
  context?: ContextStore;
  userAuth?: UserAuthStore;
  program?: Program;
  product?: Product;
  userProgram?: any;
}

interface BexSearchState
  extends TrackingTypeState,
    TrackingInformationState,
    TravelOptionsState,
    DestinationState,
    WidthState,
    HeightState,
    BackgroundState,
    OpenLinkState,
    ErrorsState,
    DemoLinkState,
    DemoStylesState {}

@withLocalization
@inject('analytics', 'context')
@observer
class BexSearch extends React.Component<BexSearchProps, BexSearchState> {
  private readonly specialChars = ['>', '<', '&', "'", '"'];

  private readonly initialState: BexSearchState;

  constructor(props) {
    super(props);

    this.initialState = {
      ...initialTrackingTypeState,
      ...initialTrackingInformationState,
      ...initialTravelOptionsState,
      ...initialDestinationState,
      ...initialFixedWidthState,
      ...initialFixedHeightState,
      ...initialBackgroundState,
      ...initialOpenLinkState,
      ...initialErrorsState,
    };

    this.state = {
      ...this.initialState,
      ...this.getDemoLinkState(this.initialState),
      ...this.getDemoStylesState(this.initialState),
    };

    this.handleTrackingTypeSelectChange = this.handleTrackingTypeSelectChange.bind(this);

    this.handleTrackingInformationCJPIDInputChange = this.handleTrackingInformationCJPIDInputChange.bind(this);
    this.handleTrackingInformationCJSIDInputChange = this.handleTrackingInformationCJSIDInputChange.bind(this);

    this.handleTravelOptionsInputChange = this.handleTravelOptionsInputChange.bind(this);

    this.handleDestinationInputChange = this.handleDestinationInputChange.bind(this);

    this.handleWidthSizingSelectChange = this.handleWidthSizingSelectChange.bind(this);
    this.handleWidthSizeInputChange = this.handleWidthSizeInputChange.bind(this);

    this.handleHeightSizingSelectChange = this.handleHeightSizingSelectChange.bind(this);
    this.handleHeightSizeInputChange = this.handleHeightSizeInputChange.bind(this);

    this.handleBackgroundSelectChange = this.handleBackgroundSelectChange.bind(this);

    this.handleOpenLinkInputChange = this.handleOpenLinkInputChange.bind(this);

    this.handleFormSubmit = this.handleFormSubmit.bind(this);
    this.handleFormReset = this.handleFormReset.bind(this);
  }

  private copyToClipboardLabel = this.props.l10n.formatText('product.search.code.actions.copy_to_clipboard');
  private copiedToClipboardLabel = this.props.l10n.formatText('product.search.code.actions.copied_to_clipboard');

  private pasteToClipboard(copyText) {
    navigator.clipboard.writeText(copyText);

    const copyButton = document.getElementById('code-copy');
    copyButton.textContent = this.copiedToClipboardLabel;
    setTimeout(() => {
      copyButton.textContent = this.copyToClipboardLabel;
    }, 1500);
  }

  private getDemoLinkState(state: BexSearchState): DemoLinkState {
    const {
      trackingType,
      trackingInformationCJPID,
      trackingInformationCJSID,
      travelOptions,
      destination,
      widthSizing,
      widthSize,
      heightSizing,
      heightSize,
      background,
      openLink,
    } = state;

    const origin = 'https://www.expedia.com';
    const widgetsPathname = '/marketing/widgets';
    const widgetPathname = '/searchform/widget';

    const widgetSearchParams = new URLSearchParams({
      wtt: trackingType,
      tp1: trackingInformationCJPID || '000000',
      tp2: trackingInformationCJSID,
      lob: travelOptions.join(),
      des: destination,
      wbi: background,
      olc: '000000',
      whf: '4',
      hfc: 'C7C7C7',
      wif: '4',
      ifc: '000000',
      wbc: 'FFCB00',
      wbf: '4',
      bfc: '3D3100',
      wws: openLink,
      sfs: `H${heightSize}${heightSizing}W${widthSize}${widthSizing}`,
      langid: '1033',
    });

    const widgetSearch = `?${decodeURIComponent(`${widgetSearchParams}`)}`;

    const demoLink = `${origin}${widgetsPathname}${widgetPathname}${widgetSearch}`;

    return { demoLink };
  }

  private getDemoStylesState(state: BexSearchState): DemoStylesState {
    const { widthSize, widthUnit, heightSizing, heightSize, heightUnit } = state;

    const { heightSizeMax: initialFixedHeightSizeMax } = initialFixedHeightState;

    const widgetBaseWidthStyle = `${widthSize}${widthUnit}`;
    const widgetBaseHeightStyle = `${heightSize}${heightUnit}`;

    const widgetFakeHeightStyle =
      heightSizing === DimensionSizing.RELATIVE &&
      `${(heightSize / 100) * initialFixedHeightSizeMax}${DimensionUnit.FIXED}`;

    return {
      demoPreviewWidgetStyles: {
        width: widgetBaseWidthStyle,
        height: widgetFakeHeightStyle || widgetBaseHeightStyle,
      },
      demoCodeMarkupWidgetStyles: `width:${widgetBaseWidthStyle};height:${widgetBaseHeightStyle};`,
    };
  }

  private handleTrackingTypeSelectChange(event: React.ChangeEvent<HTMLSelectElement>) {
    const { l10n } = this.props;
    const {
      value,
      validity: { valid, valueMissing },
    } = event.target;

    this.setState((prevState) => {
      const {
        errors: { trackingType: prevTrackingTypeError, ...otherErrors },
      } = prevState;

      const trackingType = value as TrackingType;

      const errors = {
        ...otherErrors,
        ...(!valid && {
          trackingType:
            valueMissing && l10n.formatText('product.bex-search.configure.form.tracking-type.error.you_must_select'),
        }),
      };

      return { trackingType, errors };
    });
  }

  private handleTrackingInformationCJPIDInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { l10n } = this.props;
    const {
      value,
      validity: { valid, valueMissing, patternMismatch },
    } = event.target;

    this.setState((prevState) => {
      const {
        errors: { trackingInformationCJPID: prevTrackingInformationCJPIDError, ...otherErrors },
      } = prevState;

      const trackingInformationCJPID = `${value}`;

      const errors = {
        ...otherErrors,
        ...((!valid || trackingInformationCJPID === '') && {
          trackingInformationCJPID:
            ((valueMissing || trackingInformationCJPID === '') &&
              l10n.formatText(
                'product.bex-search.configure.form.tracking-information.cj.pid.error.promotional_property_id_pid_mandatory',
              )) ||
            (patternMismatch &&
              l10n.formatText(
                'product.bex-search.configure.form.common.error.special_chars_invalid',
                this.specialChars.join(' '),
              )),
        }),
      };

      return { trackingInformationCJPID, errors };
    });
  }

  private handleTrackingInformationCJSIDInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { l10n } = this.props;
    const {
      value,
      validity: { valid, patternMismatch },
    } = event.target;

    this.setState((prevState) => {
      const {
        errors: { trackingInformationCJSID: prevTrackingInformationCJSIDError, ...otherErrors },
      } = prevState;

      const trackingInformationCJSID = `${value}`;

      const errors = {
        ...otherErrors,
        ...(!valid && {
          trackingInformationCJSID:
            patternMismatch &&
            l10n.formatText(
              'product.bex-search.configure.form.common.error.special_chars_invalid',
              this.specialChars.join(' '),
            ),
        }),
      };

      return { trackingInformationCJSID, errors };
    });
  }

  private handleTravelOptionsInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { l10n } = this.props;
    const { checked, value } = event.target;

    this.setState((prevState) => {
      const {
        travelOptions: prevTravelOptions,
        errors: { travelOptions: prevTravelOptionsError, ...otherErrors },
      } = prevState;

      const travelOption = value as TravelOption;

      const travelOptions = checked
        ? [...prevTravelOptions, travelOption]
        : prevTravelOptions.filter((prevTravelOption) => prevTravelOption !== travelOption);

      const valueMissing = travelOptions.length === 0;
      const valid = !valueMissing;

      const errors = {
        ...otherErrors,
        ...(!valid && {
          travelOptions: l10n.formatText('product.bex-search.configure.form.travel-options.error.please_select_one'),
        }),
      };

      return { travelOptions, errors };
    });
  }

  private handleDestinationInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { l10n } = this.props;
    const {
      value,
      validity: { valid, patternMismatch },
    } = event.target;

    this.setState((prevState) => {
      const {
        errors: { destination: prevDestinationError, ...otherErrors },
      } = prevState;

      const destination = `${value}`;

      const errors = {
        ...otherErrors,
        ...(!valid && {
          destination:
            patternMismatch &&
            l10n.formatText(
              'product.bex-search.configure.form.common.error.special_chars_invalid',
              this.specialChars.join(' '),
            ),
        }),
      };

      return { destination, errors };
    });
  }

  private handleWidthSizingSelectChange(event: React.ChangeEvent<HTMLSelectElement>) {
    const { value } = event.target;

    this.setState((prevState) => {
      const {
        errors: { widthSize: prevWidthSizeError, ...otherErrors },
      } = prevState;

      const dimensionSizing = value as DimensionSizing;

      const initialWidthState = initialWidthStates[dimensionSizing];

      const errors = { ...otherErrors };

      return { ...initialWidthState, errors };
    });
  }

  private handleWidthSizeInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { l10n } = this.props;
    const {
      valueAsNumber,
      validity: { valid },
    } = event.target;

    this.setState((prevState) => {
      const {
        widthSizing,
        widthSizeMin,
        widthSizeMax,
        errors: { widthSize: prevWidthSizeError, ...otherErrors },
      } = prevState;

      const widthSize = valueAsNumber || undefined;

      const errors = {
        ...otherErrors,
        ...((!valid || isNaN(valueAsNumber)) && {
          widthSize:
            (widthSizing === DimensionSizing.FIXED &&
              l10n.formatText(
                'product.bex-search.configure.form.dimension.size.fixed.error.provide_correct_size',
                widthSizeMin,
                widthSizeMax,
              )) ||
            (widthSizing === DimensionSizing.RELATIVE &&
              l10n.formatText(
                'product.bex-search.configure.form.dimension.size.relative.error.provide_correct_percentage',
              )),
        }),
      };

      return { widthSize, errors };
    });
  }

  private handleHeightSizingSelectChange(event: React.ChangeEvent<HTMLSelectElement>) {
    const { value } = event.target;

    this.setState((prevState) => {
      const {
        errors: { heightSize: prevHeightSizeError, ...otherErrors },
      } = prevState;

      const dimensionSizing = value as DimensionSizing;

      const initialHeightState = initialHeightStates[dimensionSizing];

      const errors = { ...otherErrors };

      return { ...initialHeightState, errors };
    });
  }

  private handleHeightSizeInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { l10n } = this.props;
    const {
      valueAsNumber,
      validity: { valid },
    } = event.target;

    this.setState((prevState) => {
      const {
        heightSizing,
        heightSizeMin,
        heightSizeMax,
        errors: { heightSize: prevHeightSizeError, ...otherErrors },
      } = prevState;

      const heightSize = valueAsNumber || undefined;

      const errors = {
        ...otherErrors,
        ...((!valid || isNaN(valueAsNumber)) && {
          heightSize:
            (heightSizing === DimensionSizing.FIXED &&
              l10n.formatText(
                'product.bex-search.configure.form.dimension.size.fixed.error.provide_correct_size',
                heightSizeMin,
                heightSizeMax,
              )) ||
            (heightSizing === DimensionSizing.RELATIVE &&
              l10n.formatText(
                'product.bex-search.configure.form.dimension.size.relative.error.provide_correct_percentage',
              )),
        }),
      };

      return { heightSize, errors };
    });
  }

  private handleBackgroundSelectChange(event: React.ChangeEvent<HTMLSelectElement>) {
    const { value } = event.target;

    const background = value as Background;

    this.setState({ background });
  }

  private handleOpenLinkInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { value } = event.target;

    const openLink = value as OpenLink;

    this.setState({ openLink });
  }

  private handleFormSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    this.analyticsTrackEvents('affhub_code_create.selected');

    const { errors } = this.state;

    if (!Object.keys(errors).length) {
      this.setState({
        ...this.getDemoLinkState(this.state),
        ...this.getDemoStylesState(this.state),
      });
    }
  }

  private handleFormReset(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    this.setState({
      ...this.initialState,
      ...this.getDemoLinkState(this.initialState),
      ...this.getDemoStylesState(this.initialState),
    });
  }

  private analyticsTrackEvents = (rfrr: string) => {
    const { analytics, program, product, userProgram } = this.props;

    if (typeof analytics?.trackEvent === 'function') {
      const microMessages = [
        getProgramAnalyticsMicroMessage(program, userProgram.programStatus),
        getProductAnalyticsMicroMessage(product),
      ];
      const filterAnalyticsTrackEvent = {
        'affhub.product_config': () => {
          analytics.trackEvent(rfrr, 'AffHub Product Config Presented', null, null, true, microMessages);
        },
        'affhub_copy_code.selected': () => {
          analytics.trackEvent(rfrr, 'AffHub Copy Code Selected', null, null, true, microMessages);
        },
        'affhub_code_create.selected': () => {
          analytics.trackEvent(rfrr, 'AffHub Code Create Selected', null, null, true, microMessages);
        },
      };

      filterAnalyticsTrackEvent[rfrr]();
    }
  };

  public componentDidMount() {
    this.analyticsTrackEvents('affhub.product_config');
  }

  public render() {
    const { l10n, product } = this.props;
    const {
      trackingType,
      trackingInformationCJPID,
      trackingInformationCJSID,
      travelOptions,
      destination,
      widthSizing,
      widthSize,
      widthSizeMin,
      widthSizeMax,
      widthSizeStep,
      heightSizing,
      heightSize,
      heightSizeMin,
      heightSizeMax,
      heightSizeStep,
      background,
      openLink,
      errors,
      demoLink,
      demoPreviewWidgetStyles,
      demoCodeMarkupWidgetStyles,
    } = this.state;

    return (
      <main className="container">
        <UitkLayoutGrid columns={{ large: 2 }} space="twelve">
          <UitkLayoutGridItem>
            <div>
              <section>
                <UitkSpacing margin={{ blockend: 'four' }}>
                  <UitkHeading tag="h2" size={4}>
                    <LocalizedText message="product.bex-search.title.configure" />
                  </UitkHeading>
                </UitkSpacing>

                <form noValidate={true} onSubmit={this.handleFormSubmit} onReset={this.handleFormReset}>
                  <UitkLayoutGrid space="six">
                    <UitkLayoutGridItem>
                      <div hidden={true}>
                        {/* #trackingTypeOuter */}
                        <fieldset>
                          <UitkSpacing margin={{ blockend: 'two' }}>
                            <legend>
                              <UitkHeading tag="h3" size={6}>
                                <LocalizedText message="product.bex-search.configure.form.title.tracking_type" />
                              </UitkHeading>
                            </legend>
                          </UitkSpacing>

                          {/* #trackingType */}
                          <UitkSelect
                            id="tracking-type"
                            name="wtt"
                            label={l10n.formatText('product.bex-search.configure.form.label.tracking_type')}
                            // placeholder={l10n.formatText('product.bex-search.configure.form.placeholder.tracking_type')}
                            value={trackingType}
                            onChange={this.handleTrackingTypeSelectChange}
                            required={true}
                            invalid={errors.trackingType}
                            hasVisibleLabel={false}
                          >
                            <option value={TrackingType.NONE}>
                              {l10n.formatText('product.bex-search.configure.form.tracking-type.label.select_network')}
                            </option>

                            <optgroup
                              label={l10n.formatText('product.bex-search.configure.form.tracking-type.label.networks')}
                            >
                              <option value={TrackingType.CJ}>
                                {l10n.formatText('product.bex-search.configure.form.tracking-type.networks.label.cj')}
                              </option>
                              <option value={TrackingType.CB} disabled={true} hidden={true}>
                                {l10n.formatText('product.bex-search.configure.form.tracking-type.networks.label.cb')}
                              </option>
                              <option value={TrackingType.OLA} disabled={true} hidden={true}>
                                {l10n.formatText('product.bex-search.configure.form.tracking-type.networks.label.ola')}
                              </option>
                              <option value={TrackingType.DP} disabled={true} hidden={true}>
                                {l10n.formatText('product.bex-search.configure.form.tracking-type.networks.label.dp')}
                              </option>
                              <option value={TrackingType.PZ} hidden={true}>
                                {l10n.formatText('product.bex-search.configure.form.tracking-type.networks.label.pz')}
                              </option>
                            </optgroup>
                          </UitkSelect>
                        </fieldset>
                      </div>
                    </UitkLayoutGridItem>

                    {trackingType !== TrackingType.NONE && (
                      <UitkLayoutGridItem>
                        <div>
                          {/* #trackingTypeInner */}
                          <fieldset>
                            {/* #cjTracking */}
                            {trackingType === TrackingType.CJ && (
                              <>
                                <UitkSpacing margin={{ blockend: 'two' }}>
                                  <legend>
                                    <UitkHeading tag="h3" size={6}>
                                      <LocalizedText message="product.bex-search.configure.form.title.cj_tracking_information" />
                                    </UitkHeading>
                                  </legend>
                                </UitkSpacing>

                                <UitkLayoutGrid space="three">
                                  <UitkLayoutGridItem>
                                    <div>
                                      {/* #pid */}
                                      <UitkInformativeInput
                                        id="tracking-cj-pid"
                                        name="tp1"
                                        label={l10n.formatText(
                                          'product.bex-search.configure.form.tracking-information.cj.label.promotional_property_id_pid',
                                        )}
                                        informativeTrigger={<MoreInformationDialogTriggerButton id="cj-pid" />}
                                        a11yInformativeTrigger={l10n.formatText(
                                          'more-information.dialog.trigger.more_information',
                                        )}
                                        value={trackingInformationCJPID}
                                        onChange={this.handleTrackingInformationCJPIDInputChange}
                                        required={true}
                                        pattern={`[^${this.specialChars.join('')}]*`}
                                        invalid={errors.trackingInformationCJPID}
                                      />
                                      <MoreInformationDialog
                                        id="cj-pid"
                                        title={l10n.formatText(
                                          'more-information.dialog.cj-pid.title.what_promotional_property_id',
                                        )}
                                        description={l10n.formatText(
                                          'more-information.dialog.cj-pid.description.signup_cj_your_website_assigned_id_pid_each_website_add_your_account_own_unique_id',
                                        )}
                                      />
                                    </div>
                                  </UitkLayoutGridItem>

                                  <UitkLayoutGridItem>
                                    <div>
                                      {/* #sid */}
                                      <UitkInformativeInput
                                        id="tracking-cj-sid"
                                        name="tp2"
                                        label={l10n.formatText(
                                          'product.bex-search.configure.form.tracking-information.cj.label.shopper_id_sid',
                                        )}
                                        informativeTrigger={<MoreInformationDialogTriggerButton id="cj-sid" />}
                                        a11yInformativeTrigger={l10n.formatText(
                                          'more-information.dialog.trigger.more_information',
                                        )}
                                        value={trackingInformationCJSID}
                                        onChange={this.handleTrackingInformationCJSIDInputChange}
                                        pattern={`[^${this.specialChars.join('')}]*`}
                                        invalid={errors.trackingInformationCJSID}
                                      />
                                      <MoreInformationDialog
                                        id="cj-sid"
                                        title={l10n.formatText('more-information.dialog.cj-sid.title.what_shopper_id')}
                                        description={l10n.formatText(
                                          'more-information.dialog.cj-sid.description.shopper_id_sid_optional_custom_tag_create_add_to_link_for_your_own_tracking_purposes',
                                        )}
                                      />
                                    </div>
                                  </UitkLayoutGridItem>
                                </UitkLayoutGrid>
                              </>
                            )}

                            {/* #cobrandTracking */}
                            {/*{trackingType === TrackingType.CB && ( ... )}*/}

                            {/* #olaTracking */}
                            {/*{trackingType === TrackingType.OLA && ( ... )}*/}

                            {/* #directPartnerTracking */}
                            {/*{trackingType === TrackingType.DP && ( ... )}*/}

                            {/* #phgTracking */}
                            {/*{trackingType === TrackingType.PZ && ( ... )}*/}
                          </fieldset>
                        </div>
                      </UitkLayoutGridItem>
                    )}

                    <UitkLayoutGridItem>
                      <div>
                        {/* #lobType */}
                        <fieldset>
                          <UitkSpacing>
                            <legend>
                              <UitkHeading tag="h3" size={6}>
                                <LocalizedText message="product.bex-search.configure.form.travel-options.title.travel_options" />
                              </UitkHeading>
                            </legend>
                          </UitkSpacing>

                          <UitkCheckboxGroup group="lob" errorMessage={errors.travelOptions}>
                            <UitkLayoutGrid columns={{ small: 2, medium: 3 }}>
                              <UitkLayoutGridItem>
                                <div>
                                  {/* #lobHotel */}
                                  <UitkCheckbox
                                    id="lob-hotels"
                                    name="lob"
                                    value={TravelOption.HOTELS}
                                    checked={travelOptions.includes(TravelOption.HOTELS)}
                                    onChange={this.handleTravelOptionsInputChange}
                                  >
                                    <LocalizedText message="product.bex-search.configure.form.travel-options.label.hotels" />
                                  </UitkCheckbox>
                                </div>
                              </UitkLayoutGridItem>

                              <UitkLayoutGridItem>
                                <div>
                                  {/* #lobFlightHotel */}
                                  <UitkCheckbox
                                    id="lob-flights-hotels"
                                    name="lob"
                                    value={TravelOption.FLIGHTS_HOTELS}
                                    checked={travelOptions.includes(TravelOption.FLIGHTS_HOTELS)}
                                    onChange={this.handleTravelOptionsInputChange}
                                  >
                                    <LocalizedText message="product.bex-search.configure.form.travel-options.label.flights_hotels" />
                                  </UitkCheckbox>
                                </div>
                              </UitkLayoutGridItem>

                              <UitkLayoutGridItem>
                                <div>
                                  {/* #lobFlight */}
                                  <UitkCheckbox
                                    id="lob-flights"
                                    name="lob"
                                    value={TravelOption.FLIGHTS}
                                    checked={travelOptions.includes(TravelOption.FLIGHTS)}
                                    onChange={this.handleTravelOptionsInputChange}
                                  >
                                    <LocalizedText message="product.bex-search.configure.form.travel-options.label.flights" />
                                  </UitkCheckbox>
                                </div>
                              </UitkLayoutGridItem>

                              <UitkLayoutGridItem>
                                <div>
                                  {/* #lobCar */}
                                  <UitkCheckbox
                                    id="lob-cars"
                                    name="lob"
                                    value={TravelOption.CARS}
                                    checked={travelOptions.includes(TravelOption.CARS)}
                                    onChange={this.handleTravelOptionsInputChange}
                                  >
                                    <LocalizedText message="product.bex-search.configure.form.travel-options.label.cars" />
                                  </UitkCheckbox>
                                </div>
                              </UitkLayoutGridItem>

                              <UitkLayoutGridItem>
                                <div>
                                  {/* #lobCruise */}
                                  <UitkCheckbox
                                    id="lob-cruises"
                                    name="lob"
                                    value={TravelOption.CRUISES}
                                    checked={travelOptions.includes(TravelOption.CRUISES)}
                                    onChange={this.handleTravelOptionsInputChange}
                                  >
                                    <LocalizedText message="product.bex-search.configure.form.travel-options.label.cruises" />
                                  </UitkCheckbox>
                                </div>
                              </UitkLayoutGridItem>

                              <UitkLayoutGridItem>
                                <div>
                                  {/* #lobActivities */}
                                  <UitkCheckbox
                                    id="lob-activities"
                                    name="lob"
                                    value={TravelOption.ACTIVITIES}
                                    checked={travelOptions.includes(TravelOption.ACTIVITIES)}
                                    onChange={this.handleTravelOptionsInputChange}
                                  >
                                    <LocalizedText message="product.bex-search.configure.form.travel-options.label.activities" />
                                  </UitkCheckbox>
                                </div>
                              </UitkLayoutGridItem>
                            </UitkLayoutGrid>
                          </UitkCheckboxGroup>
                        </fieldset>
                      </div>
                    </UitkLayoutGridItem>

                    <UitkLayoutGridItem>
                      <div>
                        <fieldset>
                          <UitkSpacing margin={{ blockend: 'two' }}>
                            <legend>
                              <UitkHeading tag="h3" size={6}>
                                <LocalizedText message="product.bex-search.configure.form.destination.title.default_destination" />
                              </UitkHeading>
                            </legend>
                          </UitkSpacing>

                          {/* #destination */}
                          <UitkTextInput
                            id="destination"
                            name="des"
                            icon="place"
                            label={l10n.formatText('product.bex-search.configure.form.label.destination')}
                            placeholder={l10n.formatText('product.bex-search.configure.form.placeholder.destination')}
                            value={destination}
                            onChange={this.handleDestinationInputChange}
                            pattern={`[^${this.specialChars.join('')}]*`}
                            invalid={errors.destination}
                            hasClearButton={true}
                            hasVisibleLabel={false}
                          />
                        </fieldset>
                      </div>
                    </UitkLayoutGridItem>

                    <UitkLayoutGridItem>
                      <div>
                        <fieldset>
                          <UitkSpacing margin={{ blockend: 'two' }}>
                            <legend>
                              <UitkHeading tag="h3" size={6}>
                                <LocalizedText message="product.bex-search.configure.form.title.width" />
                                {/*{`(min: ${widthSizeMin}px | max: ${widthSizeMax}px)`}*/}
                              </UitkHeading>
                            </legend>
                          </UitkSpacing>

                          <UitkLayoutGrid columns={2} space="three">
                            <UitkLayoutGridItem>
                              <div>
                                {/* #widthOption */}
                                <UitkSelect
                                  id="width-sizing"
                                  name="sfs-wt"
                                  label={l10n.formatText('product.bex-search.configure.form.width.label.width_sizing')}
                                  value={widthSizing}
                                  onChange={this.handleWidthSizingSelectChange}
                                  hasVisibleLabel={false}
                                >
                                  <option value={DimensionSizing.FIXED}>
                                    {l10n.formatText('product.bex-search.configure.form.dimension.sizing.label.fixed')}
                                  </option>
                                  <option value={DimensionSizing.RELATIVE}>
                                    {l10n.formatText(
                                      'product.bex-search.configure.form.dimension.sizing.label.relative',
                                    )}
                                  </option>
                                </UitkSelect>
                              </div>
                            </UitkLayoutGridItem>

                            <UitkLayoutGridItem>
                              <div>
                                {/* #widgetWidth */}
                                <UitkNumberInput
                                  id="width-size"
                                  name="sfs-wv"
                                  label={l10n.formatText('product.bex-search.configure.form.width.label.width_size')}
                                  value={widthSize}
                                  onChange={this.handleWidthSizeInputChange}
                                  required={true}
                                  min={widthSizeMin}
                                  max={widthSizeMax}
                                  // step="any"
                                  step={widthSizeStep}
                                  invalid={errors.widthSize}
                                  hasVisibleLabel={false}
                                />
                              </div>
                            </UitkLayoutGridItem>

                            {/*<UitkLayoutGridItem>*/}
                            {/*  <div>*/}
                            {/*    /!* #widthOption *!/*/}
                            {/*    <UitkSelect*/}
                            {/*      id="width-sizing"*/}
                            {/*      name="sfs-wt"*/}
                            {/*      label={l10n.formatText('product.bex-search.configure.form.width.label.width_sizing')}*/}
                            {/*      value={widthSizing}*/}
                            {/*      onChange={this.handleWidthSizingSelectChange}*/}
                            {/*      hasVisibleLabel={false}*/}
                            {/*    >*/}
                            {/*      <option value={DimensionSizing.FIXED}>{'px'}</option>*/}
                            {/*      <option value={DimensionSizing.RELATIVE}>{'%'}</option>*/}
                            {/*    </UitkSelect>*/}
                            {/*  </div>*/}
                            {/*</UitkLayoutGridItem>*/}
                          </UitkLayoutGrid>
                        </fieldset>
                      </div>
                    </UitkLayoutGridItem>

                    <UitkLayoutGridItem>
                      <div>
                        <fieldset>
                          <UitkSpacing margin={{ blockend: 'two' }}>
                            <legend>
                              <UitkHeading tag="h3" size={6}>
                                <LocalizedText message="product.bex-search.configure.form.title.height" />
                                {/*{`(min: ${heightSizeMin}px | max: ${heightSizeMax}px)`}*/}
                              </UitkHeading>
                            </legend>
                          </UitkSpacing>

                          <UitkLayoutGrid columns={2} space="three">
                            <UitkLayoutGridItem>
                              <div>
                                {/* #heightOption */}
                                <UitkSelect
                                  id="height-sizing"
                                  name="sfs-ht"
                                  label={l10n.formatText(
                                    'product.bex-search.configure.form.height.label.height_sizing',
                                  )}
                                  value={heightSizing}
                                  onChange={this.handleHeightSizingSelectChange}
                                  hasVisibleLabel={false}
                                >
                                  <option value={DimensionSizing.FIXED}>
                                    {l10n.formatText('product.bex-search.configure.form.dimension.sizing.label.fixed')}
                                  </option>
                                  <option value={DimensionSizing.RELATIVE}>
                                    {l10n.formatText(
                                      'product.bex-search.configure.form.dimension.sizing.label.relative',
                                    )}
                                  </option>
                                </UitkSelect>
                              </div>
                            </UitkLayoutGridItem>

                            <UitkLayoutGridItem>
                              <div>
                                {/* #widgetHeight */}
                                <UitkNumberInput
                                  id="height-size"
                                  name="sfs-hv"
                                  label={l10n.formatText('product.bex-search.configure.form.height.label.height_size')}
                                  value={heightSize}
                                  onChange={this.handleHeightSizeInputChange}
                                  required={true}
                                  min={heightSizeMin}
                                  max={heightSizeMax}
                                  // step="any"
                                  step={heightSizeStep}
                                  invalid={errors.heightSize}
                                  hasVisibleLabel={false}
                                />
                              </div>
                            </UitkLayoutGridItem>

                            {/*<UitkLayoutGridItem>*/}
                            {/*  <div>*/}
                            {/*    /!* #heightOption *!/*/}
                            {/*    <UitkSelect*/}
                            {/*      id="height-sizing"*/}
                            {/*      name="sfs-ht"*/}
                            {/*      label={l10n.formatText('product.bex-search.configure.form.height.label.height_sizing')}*/}
                            {/*      value={heightSizing}*/}
                            {/*      onChange={this.handleHeightSizingSelectChange}*/}
                            {/*      hasVisibleLabel={false}*/}
                            {/*    >*/}
                            {/*      <option value={DimensionSizing.FIXED}>{'px'}</option>*/}
                            {/*      <option value={DimensionSizing.RELATIVE}>{'%'}</option>*/}
                            {/*    </UitkSelect>*/}
                            {/*  </div>*/}
                            {/*</UitkLayoutGridItem>*/}
                          </UitkLayoutGrid>
                        </fieldset>
                      </div>
                    </UitkLayoutGridItem>

                    <UitkLayoutGridItem>
                      <div>
                        {/* #backgroundImageList */}
                        <fieldset>
                          <UitkSpacing margin={{ blockend: 'two' }}>
                            <legend>
                              <UitkHeading tag="h3" size={6}>
                                <LocalizedText message="product.bex-search.configure.form.title.background" />
                              </UitkHeading>
                            </legend>
                          </UitkSpacing>

                          {/* #backgroundImageDropDown */}
                          <UitkSelect
                            id="background"
                            name="wbi"
                            label={l10n.formatText('product.bex-search.configure.form.label.background')}
                            hasVisibleLabel={false}
                            value={background}
                            onChange={this.handleBackgroundSelectChange}
                          >
                            <optgroup
                              label={l10n.formatText('product.bex-search.configure.form.background.label.images')}
                            >
                              <option value={Background.BEACH}>
                                {l10n.formatText('product.bex-search.configure.form.background.images.beach')}
                              </option>
                              <option value={Background.CITY}>
                                {l10n.formatText('product.bex-search.configure.form.background.images.city')}
                              </option>
                              <option value={Background.COUNTRYSIDE}>
                                {l10n.formatText('product.bex-search.configure.form.background.images.countryside')}
                              </option>
                              <option value={Background.MOUNTAINS}>
                                {l10n.formatText('product.bex-search.configure.form.background.images.mountains')}
                              </option>
                              <option value={Background.NATURE}>
                                {l10n.formatText('product.bex-search.configure.form.background.images.nature')}
                              </option>
                              <option value={Background.NIGHTLIFE}>
                                {l10n.formatText('product.bex-search.configure.form.background.images.nightlife')}
                              </option>
                              <option value={Background.SKI}>
                                {l10n.formatText('product.bex-search.configure.form.background.images.ski')}
                              </option>
                              <option value={Background.SKY}>
                                {l10n.formatText('product.bex-search.configure.form.background.images.sky')}
                              </option>
                              <option value={Background.SNOW}>
                                {l10n.formatText('product.bex-search.configure.form.background.images.snow')}
                              </option>
                              <option value={Background.ACTIVITIES}>
                                {l10n.formatText('product.bex-search.configure.form.background.images.activities')}
                              </option>
                              <option value={Background.CARS}>
                                {l10n.formatText('product.bex-search.configure.form.background.images.cars')}
                              </option>
                              <option value={Background.CRUISE}>
                                {l10n.formatText('product.bex-search.configure.form.background.images.cruise')}
                              </option>
                              <option value={Background.FLIGHTS}>
                                {l10n.formatText('product.bex-search.configure.form.background.images.flights')}
                              </option>
                              <option value={Background.HOTELS}>
                                {l10n.formatText('product.bex-search.configure.form.background.images.hotels')}
                              </option>
                              <option value={Background.PACKAGES}>
                                {l10n.formatText('product.bex-search.configure.form.background.images.packages')}
                              </option>
                            </optgroup>

                            {/*<optgroup*/}
                            {/*  label={l10n.formatText('product.bex-search.configure.form.background.label.colors')}*/}
                            {/*>*/}
                            {/*  <option value={Background.COLOR} disabled={true}>*/}
                            {/*    {l10n.formatText('product.bex-search.configure.form.background.colors.solid')}*/}
                            {/*  </option>*/}
                            {/*</optgroup>*/}
                          </UitkSelect>
                        </fieldset>
                      </div>
                    </UitkLayoutGridItem>

                    {/* Overlay Color */}

                    {/* Header Font and Font Color */}

                    {/* Input Field Font and Font Color */}

                    {/* Button Color */}

                    {/* Button Font and Font Color */}

                    <UitkLayoutGridItem>
                      <div>
                        {/* #widgetOpen */}
                        <fieldset>
                          <UitkSpacing>
                            <legend>
                              <UitkHeading tag="h3" size={6}>
                                <LocalizedText message="product.bex-search.configure.form.open-link.title.search_link_opens" />
                              </UitkHeading>
                            </legend>
                          </UitkSpacing>

                          {/* #widgetOpenStyle */}
                          <UitkRadioGroup group="wws" selected={openLink} onChange={this.handleOpenLinkInputChange}>
                            {/* #widgetOpenStyleSameWindow */}
                            <UitkRadio
                              id="open-link-same-window"
                              value={OpenLink.SAME_WINDOW}
                              checked={openLink === OpenLink.SAME_WINDOW}
                            >
                              <LocalizedText message="product.bex-search.configure.form.open-link.same_window" />
                            </UitkRadio>

                            {/* #widgetOpenStyleNewWindow */}
                            <UitkRadio
                              id="open-link-new-window"
                              value={OpenLink.NEW_WINDOW}
                              checked={openLink === OpenLink.NEW_WINDOW}
                            >
                              <LocalizedText message="product.bex-search.configure.form.open-link.new_window" />
                            </UitkRadio>
                          </UitkRadioGroup>
                        </fieldset>
                      </div>
                    </UitkLayoutGridItem>

                    <UitkLayoutGridItem>
                      <div>
                        <fieldset>
                          <UitkSpacing>
                            <legend className="is-visually-hidden">
                              <UitkHeading tag="h3" size={6}>
                                <LocalizedText message="product.bex-search.configure.form.title.actions" />
                              </UitkHeading>
                            </legend>
                          </UitkSpacing>

                          <UitkLayoutFlex direction="row" space="three">
                            <UitkLayoutFlexItem>
                              <div>
                                {/* #submitButton */}
                                <UitkPrimaryButton type="submit">
                                  <LocalizedText message="product.bex-search.configure.form.actions.build_search_widget" />
                                </UitkPrimaryButton>
                              </div>
                            </UitkLayoutFlexItem>

                            <UitkLayoutFlexItem>
                              <div>
                                {/* #resetButton */}
                                <UitkSecondaryButton type="reset">
                                  <LocalizedText message="product.bex-search.configure.form.actions.reset" />
                                </UitkSecondaryButton>
                              </div>
                            </UitkLayoutFlexItem>
                          </UitkLayoutFlex>
                        </fieldset>
                      </div>
                    </UitkLayoutGridItem>
                  </UitkLayoutGrid>
                </form>
              </section>
            </div>
          </UitkLayoutGridItem>

          <UitkLayoutGridItem>
            <div>
              <UitkLayoutGrid space="twelve">
                <UitkLayoutGridItem>
                  <div>
                    <section>
                      <UitkHeading tag="h2" size={4}>
                        <LocalizedText message="product.search.preview.title" />
                      </UitkHeading>

                      {/*<UitkParagraph size={1}>*/}
                      {/*  <UitkText inline={true} theme="emphasis">*/}
                      {/*    {'Note: '}*/}
                      {/*  </UitkText>*/}
                      {/*  <UitkText inline={true}>*/}
                      {/*    {'This widget is responsive and will automatically fit its container size.'}*/}
                      {/*  </UitkText>*/}
                      {/*</UitkParagraph>*/}

                      <UitkParagraph size={1}>
                        <LocalizedText message="product.search.preview.information" />
                      </UitkParagraph>

                      {/* #widget-container */}
                      <UitkSpacing margin={{ blockstart: 'four' }}>
                        <div>
                          {/* 1.0: Preview (raw init) */}
                          {/*<div id="searchWidget" style="height:600px;width:300px;">*/}
                          {/*  <iframe id="widgetIframe" src="/marketing/widgets/searchform/preview?lob=H,FH,F&amp;wtt=2&amp;tp1=init&amp;whf=4&amp;wif=4&amp;wbf=4&amp;sfs=H600FW300F" width="100%" height="100%" scrolling="no" frameBorder="0">*/}
                          {/*  </iframe>*/}
                          {/*</div>*/}

                          {/* 1.1: Preview (clean init) */}
                          {/*<div id="searchWidget" style={{ width: '300px', height: '600px' }}>*/}
                          {/*  <iframe*/}
                          {/*    id="widgetIframe"*/}
                          {/*    src="https://www.expedia.com/marketing/widgets/searchform/preview?lob=H,FH,F&wtt=2&tp1=init&whf=4&wif=4&wbf=4&sfs=H600FW300F"*/}
                          {/*    width="100%"*/}
                          {/*    height="100%"*/}
                          {/*    frameBorder="0"*/}
                          {/*    scrolling="no"*/}
                          {/*  />*/}
                          {/*</div>*/}

                          {/* 2.0: Preview (raw build) */}
                          {/*<div id="searchWidget" style="width:300px;height:600px;">*/}
                          {/*  <iframe id="widgetIframe" src="https://www.expedia.com/marketing/widgets/searchform/preview?wtt=1&amp;tp1=123456&amp;tp2=&amp;lob=H,FH,F&amp;des=&amp;wbi=1&amp;olc=000000&amp;whf=4&amp;hfc=C7C7C7&amp;wif=4&amp;ifc=000000&amp;wbc=FFCB00&amp;wbf=4&amp;bfc=3D3100&amp;wws=1&amp;sfs=H600FW300F&amp;langid=1033" width="100%" height="100%" scrolling="no" frameBorder="0">*/}
                          {/*  </iframe>*/}
                          {/*</div>*/}

                          {/* 2.1: Preview (clean build) */}
                          {/*<div id="searchWidget" style={{ width: '300px', height: '600px' }}>*/}
                          {/*  <iframe*/}
                          {/*    id="widgetIframe"*/}
                          {/*    src="https://www.expedia.com/marketing/widgets/searchform/preview?wtt=1&tp1=123456&tp2=&lob=H,FH,F&des=&wbi=1&olc=000000&whf=4&hfc=C7C7C7&wif=4&ifc=000000&wbc=FFCB00&wbf=4&bfc=3D3100&wws=1&sfs=H600FW300F&langid=1033"*/}
                          {/*    width="100%"*/}
                          {/*    height="100%"*/}
                          {/*    frameBorder="0"*/}
                          {/*    scrolling="no"*/}
                          {/*  />*/}
                          {/*</div>*/}

                          {/* 3.0: Code (raw build) */}
                          {/*<div id="searchWidget" style="width:300px;height:600px;"><iframe id="widgetIframe" src="https://www.expedia.com/marketing/widgets/searchform/widget?wtt=1&tp1=123456&tp2=&lob=H,FH,F&des=&wbi=1&olc=000000&whf=4&hfc=C7C7C7&wif=4&ifc=000000&wbc=FFCB00&wbf=4&bfc=3D3100&wws=1&sfs=H600FW300F&langid=1033" width="100%" height="100%" scrolling="no" frameBorder="0"></iframe></div>*/}

                          {/* 3.1: Code (clean build) */}
                          {/*<div id="searchWidget" style={{ width: '300px', height: '600px' }}>*/}
                          {/*  <iframe*/}
                          {/*    id="widgetIframe"*/}
                          {/*    src="https://www.expedia.com/marketing/widgets/searchform/widget?wtt=1&tp1=000000&tp2=&lob=H,FH,F&des=&wbi=1&olc=000000&whf=4&hfc=C7C7C7&wif=4&ifc=000000&wbc=FFCB00&wbf=4&bfc=3D3100&wws=1&sfs=H600FW300F&langid=1033"*/}
                          {/*    width="100%"*/}
                          {/*    height="100%"*/}
                          {/*    frameBorder="0"*/}
                          {/*    scrolling="no"*/}
                          {/*  />*/}
                          {/*</div>*/}

                          {/* 4.0: Code (clean build) */}
                          <div key={demoLink} id="searchWidget" style={demoPreviewWidgetStyles}>
                            <iframe
                              id="widgetIframe"
                              src={demoLink}
                              width="100%"
                              height="100%"
                              frameBorder="0"
                              scrolling="no"
                              tabIndex={-1}
                            />
                          </div>
                          {/*<div id="searchWidget" style={{ width: '300px', height: '600px' }}>*/}
                          {/*  <iframe*/}
                          {/*    key={demoLink}*/}
                          {/*    id="widgetIframe"*/}
                          {/*    src="https://www.expedia.com/marketing/widgets/searchform/widget?wtt=1&tp1=000000&tp2=&lob=H,FH,F&des=&wbi=1&olc=000000&whf=4&hfc=C7C7C7&wif=4&ifc=000000&wbc=FFCB00&wbf=4&bfc=3D3100&wws=1&sfs=H600FW300F&langid=1033"*/}
                          {/*    width="100%"*/}
                          {/*    height="100%"*/}
                          {/*    frameBorder="0"*/}
                          {/*    scrolling="no"*/}
                          {/*    tabIndex={-1}*/}
                          {/*  />*/}
                          {/*</div>*/}
                        </div>
                      </UitkSpacing>
                    </section>
                  </div>
                </UitkLayoutGridItem>

                <UitkLayoutGridItem>
                  <div>
                    <section>
                      <UitkHeading tag="h2" size={4}>
                        <LocalizedText message="product.search.code.title" />
                      </UitkHeading>

                      <UitkParagraph size={1}>
                        <LocalizedText message="product.search.code.instructions" />
                      </UitkParagraph>

                      <UitkParagraph size={1}>
                        <UitkLink inline={true}>
                          <a href={product.guideLink} target="_blank">
                            <LocalizedText message="product.search.code.implementation_guide" />
                            <UitkIcon name="open_in_new" />
                          </a>
                        </UitkLink>
                      </UitkParagraph>

                      <UitkSpacing margin={{ blockstart: 'four' }}>
                        <form>
                          <UitkLayoutGrid space="two">
                            <UitkLayoutGridItem>
                              <div>
                                <fieldset>
                                  {/* #widgetsCodeArea */}
                                  <UitkTextArea
                                    key={demoLink}
                                    id="code-markup"
                                    label={l10n.formatText('product.search.code.form.markup.label')}
                                    value={`<div id="searchWidget" style="${demoCodeMarkupWidgetStyles}">\n\t<iframe id="widgetIframe" src="${demoLink}" width="100%" height="100%" frameborder="0" scrolling="no"></iframe>\n</div>`}
                                    hasVisibleLabel={false}
                                  />
                                </fieldset>
                              </div>
                            </UitkLayoutGridItem>

                            <UitkLayoutGridItem>
                              <div>
                                <fieldset>
                                  <UitkSecondaryButton
                                    id="code-copy"
                                    onClick={() => {
                                      const textArea = document.getElementById('code-markup') as HTMLTextAreaElement;
                                      textArea.focus();
                                      this.pasteToClipboard(textArea.value);
                                      this.analyticsTrackEvents('affhub_copy_code.selected');
                                    }}
                                  >
                                    {this.copyToClipboardLabel}
                                  </UitkSecondaryButton>
                                </fieldset>
                              </div>
                            </UitkLayoutGridItem>
                          </UitkLayoutGrid>
                        </form>
                      </UitkSpacing>
                    </section>
                  </div>
                </UitkLayoutGridItem>
              </UitkLayoutGrid>
            </div>
          </UitkLayoutGridItem>
        </UitkLayoutGrid>
      </main>
    );
  }
}

export { BexSearch };
